import React, { useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography,
} from '@mui/material';

export interface MergeableAccount {
  id: string;
  name: string;
  type: string;
  currency: string;
}

export interface MergeResult {
  transactions_moved: number;
  transactions_deduplicated: number;
  openings_reversed: number;
  links_moved: number;
}

/**
 * Merge a duplicate account (e.g. one Plaid created before attaching
 * existed) into the account you kept by hand (#41). Shared transactions are
 * de-duplicated, the rest move, and the duplicate is hidden.
 */
export const MergeAccountsDialog: React.FC<{
  open: boolean;
  accounts: MergeableAccount[];
  onClose: () => void;
  onMerge: (fromId: string, intoId: string) => Promise<MergeResult>;
}> = ({ open, accounts, onClose, onMerge }) => {
  const [from, setFrom] = useState('');
  const [into, setInto] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<MergeResult | null>(null);
  const fromAcct = accounts.find((a) => a.id === from);
  const targets = accounts.filter((a) => a.id !== from && (!fromAcct || (a.type === fromAcct.type && a.currency === fromAcct.currency)));
  const merge = async () => {
    setBusy(true);
    setError(null);
    try {
      setDone(await onMerge(from, into));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Merge failed');
    } finally {
      setBusy(false);
    }
  };
  const close = () => {
    setFrom('');
    setInto('');
    setDone(null);
    setError(null);
    onClose();
  };
  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Merge accounts</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Moves the duplicate&apos;s bank link and transactions into the account you keep. Transactions both already
            have are kept once; the duplicate&apos;s opening balance is reversed and it is hidden.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {done && (
            <Alert severity="success">
              Merged: {done.transactions_moved} moved, {done.transactions_deduplicated} duplicates removed,
              {' '}{done.openings_reversed} opening balance(s) reversed, {done.links_moved} bank link(s) moved.
            </Alert>
          )}
          <TextField select label="Duplicate to merge" value={from} onChange={(e) => { setFrom(e.target.value); setInto(''); }}
            inputProps={{ 'aria-label': 'Duplicate account' }}>
            {accounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
          </TextField>
          <TextField select label="Keep (merge into)" value={into} onChange={(e) => setInto(e.target.value)} disabled={!from}
            inputProps={{ 'aria-label': 'Account to keep' }}>
            {targets.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{done ? 'Close' : 'Cancel'}</Button>
        <Button variant="contained" color="warning" disabled={!from || !into || busy || !!done} onClick={() => void merge()}>
          {busy ? 'Merging…' : 'Merge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
